import { useEffect, useRef, useState } from "react";

import type { PolyfutureApiClient } from "./api.js";
import type {
  DashboardChange,
  DashboardMonitoring,
  DashboardProjection,
  TodayAttentionItem,
  TodayProjection,
  ValuationSummary,
} from "./types.js";
import { replaceProductVocabulary } from "./product-language.js";
import { ProductPage, ProgressiveDisclosure } from "./ProductSurface.js";

interface TodayViewProps {
  projection: TodayProjection;
  /** Reads the Dashboard projection (V-D1 to V-D4); absent in tests of the attention queue alone. */
  client?: PolyfutureApiClient;
  /** The portfolio whose value and monitoring are shown; the most recently updated when absent. */
  bookId?: string | undefined;
  onOpenBook: (bookId: string) => void;
  onOpenAttentionItem: (item: TodayAttentionItem) => void;
  onOpenChange?: ((change: DashboardChange) => void) | undefined;
  onCreateBook?: (() => void) | undefined;
  canCreateBook?: boolean | undefined;
  onOpenWhatIfs?: (() => void) | undefined;
}

/**
 * The v3 Dashboard (`docs/design/v3-handoff/pages/03-dashboard.md`): since the user's last visit,
 * what needs them, what changed, what the portfolio is worth and how fresh its prices are, and what
 * is and is not being watched. Everything shown comes from the server.
 */
export function TodayView({
  projection,
  client,
  bookId,
  onOpenBook,
  onOpenAttentionItem,
  onOpenChange,
  onCreateBook,
  canCreateBook = false,
  onOpenWhatIfs,
}: TodayViewProps) {
  const { books, basis, permissions } = projection;
  const wanting = projection.attentionItems.filter(
    (item) => item.attentionState === "action" || item.attentionState === "review",
  );
  const [dashboard, setDashboard] = useState<DashboardProjection>();
  const [dashboardError, setDashboardError] = useState<string>();
  // A visit is recorded once per time the page opens; React's development double mount keeps refs.
  const visit = useRef<Promise<string> | undefined>(undefined);

  useEffect(() => {
    if (client === undefined || books.length === 0) return;
    let current = true;
    visit.current ??= client.recordDashboardVisit().then((recorded) => recorded.since);
    void visit.current
      .then((since) => client.getDashboard({ since, ...(bookId === undefined ? {} : { bookId }) }))
      .then((loaded) => {
        if (!current) return;
        setDashboard(loaded);
        setDashboardError(undefined);
      })
      .catch((cause: unknown) => {
        if (!current) return;
        setDashboardError(
          cause instanceof Error ? cause.message : "The dashboard could not be read.",
        );
      });
    return () => {
      current = false;
    };
  }, [client, bookId, books.length]);

  if (books.length === 0) {
    return (
      <ProductPage className="pf-dashboard-page today-view">
        <header className="pf-dash-head">
          <h1>Since your last visit</h1>
        </header>
        <FirstRunChecklist canCreateBook={canCreateBook} onCreateBook={onCreateBook} />
      </ProductPage>
    );
  }

  return (
    <ProductPage className="pf-dashboard-page today-view">
      <header className="pf-dash-head">
        <h1>Since your last visit</h1>
        {dashboard === undefined ? null : <span>{sinceWords(dashboard.since)}</span>}
      </header>
      <p className="pf-dash-summary">
        {dashboard?.summary ??
          (dashboardError === undefined ? "Reading what changed…" : dashboardError)}
      </p>

      <div className="pf-dash-grid">
        <div className="pf-dash-main">
          <section className="pf-dash-section" aria-labelledby="dash-attention">
            <h2 id="dash-attention">
              Needs attention <span className="pf-dash-count">{wanting.length}</span>
            </h2>
            {wanting.length === 0 ? (
              <p className="pf-dash-empty">
                Nothing needs you right now. This reflects the rules you have set up; unmonitored
                risks can still affect your investments.
              </p>
            ) : (
              <div className="pf-dash-items">
                {wanting.map((item) => (
                  <TodayQueueItem
                    key={item.id}
                    item={item}
                    onAction={() => onOpenAttentionItem(item)}
                  />
                ))}
              </div>
            )}
          </section>

          <section className="pf-dash-section" aria-labelledby="dash-changed">
            <h2 id="dash-changed">Changed</h2>
            {dashboard === undefined ? null : dashboard.changed.length === 0 ? (
              <p className="pf-dash-empty">Nothing changed since your last visit.</p>
            ) : (
              <div className="pf-dash-card">
                {dashboard.changed.map((change) => (
                  <button
                    type="button"
                    key={change.id}
                    className="pf-dash-change"
                    onClick={() =>
                      onOpenChange === undefined
                        ? onOpenBook(change.destination.bookId)
                        : onOpenChange(change)
                    }
                  >
                    <span>
                      <strong>{change.title}</strong>
                      <small>
                        {change.meta} · {timeWords(change.at)}
                      </small>
                    </span>
                    {change.value === undefined ? null : (
                      <b className={`pf-dash-tone-${change.tone}`}>{change.value}</b>
                    )}
                  </button>
                ))}
              </div>
            )}
          </section>
        </div>

        <aside className="pf-dash-side">
          {dashboard?.portfolio === undefined ? null : (
            <PortfolioValueCard
              portfolio={dashboard.portfolio}
              onOpen={() => onOpenBook(dashboard.portfolio!.bookId)}
            />
          )}
          <section className="pf-dash-card pf-dash-monitoring" aria-label="Monitoring">
            <p className="pf-dash-caption">Monitoring</p>
            {dashboard?.monitoring === undefined ? null : (
              <MonitoringRows monitoring={dashboard.monitoring} onOpenWhatIfs={onOpenWhatIfs} />
            )}
            <ProgressiveDisclosure
              label="Monitoring boundary"
              summary={basis.evidenceMonitoring.replaceAll("_", " ")}
            >
              <p>
                <strong>
                  Evidence monitoring: {basis.evidenceMonitoring.replaceAll("_", " ")}
                </strong>
              </p>
              <p>{basis.explanation}</p>
              <small>
                Catalog as of {formatAsOf(basis.catalogAsOf)} · role {permissions.role}
              </small>
              <p>Polyfuture explains risk and evidence; it does not recommend or execute trades.</p>
            </ProgressiveDisclosure>
          </section>
        </aside>
      </div>
    </ProductPage>
  );
}

function PortfolioValueCard({
  portfolio,
  onOpen,
}: {
  portfolio: ValuationSummary;
  onOpen: () => void;
}) {
  const money = (value: number) =>
    value.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
  const change = portfolio.change;
  return (
    <section className="pf-dash-card pf-dash-value" aria-label="Portfolio value">
      <p className="pf-dash-caption">{portfolio.bookName}</p>
      <p className="pf-dash-value-figure">
        {portfolio.approximate ? "≈ " : ""}
        {money(portfolio.totalValue)}
      </p>
      {change === undefined ? (
        <p className="pf-dash-value-change">No previous close reported yet</p>
      ) : (
        <p className="pf-dash-value-change">
          <b className={`pf-dash-tone-${change.amount < 0 ? "bad" : "ok"}`}>
            {change.amount < 0 ? "−" : "+"}
            {money(Math.abs(change.amount))} · {change.percent >= 0 ? "+" : ""}
            {change.percent.toFixed(2)}%
          </b>{" "}
          since previous close
        </p>
      )}
      <hr />
      <p className="pf-dash-note">
        {portfolio.receivedAt === undefined
          ? "Not priced yet"
          : `Valued ${timeWords(portfolio.receivedAt)} · market ${portfolio.marketOpen ? "open" : "closed"}`}
      </p>
      <p className="pf-dash-note">
        {portfolio.freshness.fresh} of {portfolio.positionCount} prices current
        {portfolio.freshness.stale === 0 ? "" : ` · ${portfolio.freshness.stale} stale`}
        {portfolio.freshness.unpriced === 0 ? "" : ` · ${portfolio.freshness.unpriced} not priced`}
      </p>
      <button type="button" className="button pf-dash-open" onClick={onOpen}>
        Open portfolio
      </button>
    </section>
  );
}

function MonitoringRows({
  monitoring,
  onOpenWhatIfs,
}: {
  monitoring: DashboardMonitoring;
  onOpenWhatIfs?: (() => void) | undefined;
}) {
  const { signals } = monitoring;
  if (signals.total === 0) {
    return (
      <>
        <MonitoringRow
          tone="muted"
          label="Nothing is being watched"
          note="No evidence source is connected to this portfolio yet."
        />
        <MonitoringRow
          tone="muted"
          label={`${monitoring.unmonitoredPositions} ${monitoring.unmonitoredPositions === 1 ? "position" : "positions"} unmonitored`}
          note="A change in any of them would not reach you."
        />
        <p className="pf-dash-note">No check scheduled.</p>
      </>
    );
  }
  const firstStale = monitoring.staleSignals[0];
  return (
    <>
      <MonitoringRow
        tone="ok"
        label={`${signals.fresh} of ${signals.total} signals fresh`}
        note="Last observation within expected cadence"
      />
      {signals.stale === 0 ? null : (
        <MonitoringRow
          tone="warn"
          label={`${signals.stale} ${signals.stale === 1 ? "signal" : "signals"} stale`}
          note={
            firstStale === undefined
              ? "Overdue"
              : `${firstStale.name}${firstStale.overdueMinutes > 0 ? ` overdue by ${durationWords(firstStale.overdueMinutes)}` : " not current"}`
          }
        />
      )}
      {signals.inactive === 0 ? null : (
        <MonitoringRow
          tone="muted"
          label={`${signals.inactive} ${signals.inactive === 1 ? "signal" : "signals"} inactive`}
          note={`${monitoring.inactiveSignals[0] ?? "A signal"}: never observed, not "normal"`}
        />
      )}
      <MonitoringRow
        tone="cool"
        label={`${monitoring.whatIfsMonitored.count} ${monitoring.whatIfsMonitored.count === 1 ? "what-if" : "what-ifs"} monitored`}
        note={monitoring.whatIfsMonitored.names.slice(0, 3).join(", ") || "None linked to a signal"}
        {...(onOpenWhatIfs === undefined ? {} : { onClick: onOpenWhatIfs })}
      />
      <p className="pf-dash-note">
        {monitoring.nextCheckAt === undefined
          ? "No check scheduled."
          : `Next scheduled check ${timeWords(monitoring.nextCheckAt)}`}
      </p>
    </>
  );
}

function MonitoringRow({
  tone,
  label,
  note,
  onClick,
}: {
  tone: "ok" | "warn" | "muted" | "cool";
  label: string;
  note: string;
  onClick?: () => void;
}) {
  const content = (
    <>
      <i className={`pf-dash-dot pf-dash-dot-${tone}`} aria-hidden="true" />
      <span>
        <strong>{label}</strong>
        <small>{note}</small>
      </span>
    </>
  );
  return onClick === undefined ? (
    <div className="pf-dash-monitor-row">{content}</div>
  ) : (
    <button type="button" className="pf-dash-monitor-row" onClick={onClick}>
      {content}
    </button>
  );
}

/** "Yesterday, 6:12pm", "Today, 9:05am" or "Sep 21, 4:10pm", in the viewer's time zone. */
function sinceWords(iso: string): string {
  return timeWords(iso);
}

function timeWords(iso: string): string {
  const at = new Date(iso);
  const now = new Date();
  const time = at
    .toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })
    .replace(" ", "")
    .toLowerCase();
  const day = (value: Date) => value.toDateString();
  if (day(at) === day(now)) return `Today, ${time}`;
  const yesterday = new Date(now.getTime() - 86_400_000);
  if (day(at) === day(yesterday)) return `Yesterday, ${time}`;
  return `${at.toLocaleDateString("en-US", { month: "short", day: "numeric" })}, ${time}`;
}

function durationWords(minutes: number): string {
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  return hours < 48 ? `${hours}h ${minutes % 60}m` : `${Math.floor(hours / 24)} days`;
}

function TodayQueueItem({ item, onAction }: { item: TodayAttentionItem; onAction: () => void }) {
  const tone = item.attentionState === "action" ? "action" : "review";
  return (
    <article className={`pf-attention-row today-queue-${tone} today-severity-${item.severity}`}>
      <span className="pf-row-dot" aria-hidden="true" />
      <div>
        <small className="pf-attention-kicker">{replaceProductVocabulary(item.kicker)}</small>
        <strong>
          {item.destination.view === "investigation"
            ? replaceProductVocabulary(item.title)
            : item.title}
        </strong>
        <span>{replaceProductVocabulary(item.whyItMatters)}</span>
        <details>
          <summary>Why this is here</summary>
          <p>
            <strong>Mechanism</strong> · {replaceProductVocabulary(item.mechanism)}
          </p>
          <p>
            <strong>Materiality</strong> · {item.materiality.label} · {item.materiality.detail}
          </p>
          <small>
            <strong>Source line</strong> · {item.source.label} · {formatAsOf(item.source.asOf)} ·{" "}
            {item.owner.label} · {formatDue(item.dueAt)}
          </small>
        </details>
      </div>
      <button className="pf-row-action" type="button" onClick={onAction}>
        {attentionActionLabel(item.destination.view)}
      </button>
    </article>
  );
}

function attentionActionLabel(view: TodayAttentionItem["destination"]["view"]): string {
  return {
    book: "Open portfolio",
    assumption: "Open assumption",
    evidence: "Open evidence",
    episode: "Open episode",
    investigation: "Open research",
    run: "Open run",
  }[view];
}

function formatDue(value: string | undefined): string {
  if (value === undefined) return "No deadline set";
  const due = new Date(value);
  const prefix = due.getTime() < Date.now() ? "Overdue" : "Due";
  return `${prefix} ${new Intl.DateTimeFormat("en", {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: "UTC",
  }).format(due)} UTC`;
}

function TodayEmpty({ title, body }: { title: string; body: string }) {
  return (
    <div className="today-empty">
      <span aria-hidden="true">00</span>
      <div>
        <h3>{title}</h3>
        <p>{body}</p>
      </div>
    </div>
  );
}

function formatAsOf(value: string): string {
  return new Intl.DateTimeFormat("en", {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: "UTC",
  }).format(new Date(value));
}

/**
 * First-run Today. An organization with no governed portfolio has nothing real to report, and saying
 * that plainly is more useful than an empty dashboard that could be read as calm. The list states
 * what has to happen and in which order, and disappears as soon as a book exists.
 */
function FirstRunChecklist({
  canCreateBook,
  onCreateBook,
}: {
  canCreateBook: boolean;
  onCreateBook?: (() => void) | undefined;
}) {
  const steps = [
    {
      what: "Create a portfolio",
      why: "Give your investments a home. Choose a name, such as Retirement or Client growth strategy.",
      state: "Required first",
      action: canCreateBook && onCreateBook !== undefined ? onCreateBook : undefined,
      cta: "Create portfolio",
    },
    {
      what: "Add your holdings",
      why: "Search for the investments you own or upload a spreadsheet. Review the amounts before saving.",
      state: "Needs a portfolio",
    },
    {
      what: "Explore what-ifs and set up alerts",
      why: "Test a possible change, then choose which signals to watch. Turn on monitoring when you are ready.",
      state: "Needs holdings",
    },
  ];

  return (
    <div className="first-run">
      <TodayEmpty
        title="Nothing is being watched yet."
        body="Add your first portfolio to get started. No alerts does not mean there are no risks."
      />
      <p className="quiet-copy">0 portfolios · 0 saved holdings · monitoring not configured</p>
      <div className="first-run-steps">
        <p className="section-kicker">Three steps to get started</p>
        <ol>
          {steps.map((step, index) => (
            <li key={step.what}>
              <span className="first-run-number" aria-hidden="true">
                {String(index + 1).padStart(2, "0")}
              </span>
              <div>
                <h3>{step.what}</h3>
                <p className="first-run-state">{step.state}</p>
                <p>{step.why}</p>
                {step.action === undefined ? null : (
                  <button className="button button-primary" type="button" onClick={step.action}>
                    {step.cta}
                  </button>
                )}
              </div>
            </li>
          ))}
        </ol>
        <p className="first-run-note">
          Your dashboard fills in as you add holdings, run analyses, and set up monitoring.
        </p>
      </div>
    </div>
  );
}
